import * as React from "react";
import { useSelector } from "react-redux";
import { Spinner } from "../../common/Spinner";
import * as authSelectors from "../../auth/authSelectors";
import { OrderListRow } from "./order-list/OrderListRow";
import { SortArrows } from "../../common/SortArrows";
import OrderListFilterForm from "./order-list/OrderListFilterForm";
import { Table } from "../../../components/table-components/Table";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../../components/colors";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";
import { useListOrders } from "./service/order-api";
import moment from "moment";
import { useMemo } from "react";
import { ORDER_FILTER_STATES, useIsCustomer } from "../../../util";

interface Props {}

export const OrderList: React.FC<Props> = (props) => {
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);

  const {
    fromDate,
    toDate,
    shopId,
    searchValue,
  } = useSearchParamsManipulation();

  const [orderBy, setOrderBy] = React.useState("createdAt");
  const [orderDir, setOrderDir] = React.useState<"asc" | "desc">("desc");

  const searchValue_ = useMemo(() => {
    return searchValue ? searchValue : "";
  }, [searchValue]);
  const fromDate_ = useMemo(() => {
    return fromDate ? fromDate : moment().subtract(1, "month").format();
  }, [fromDate]);
  const toDate_ = useMemo(() => {
    return toDate ? toDate : moment().format();
  }, [toDate]);

  const isWedo = useIsCustomer("wedo");

  const { data: orders, isLoading } = useListOrders(
    organization,
    {
      filterByStates: ORDER_FILTER_STATES,
      ...(fromDate_ ? { filterByDateFrom: fromDate_ } : {}),
      ...(toDate_ ? { filterByDateTo: toDate_ } : {}),
      ...(shopId ? { filterByShop: shopId } : {}),
      ...(searchValue_ ? { filterByFreeText: searchValue_ } : {}),
      ...(isWedo ? { limit: 500 } : {}),
    },
    {
      orderBy,
      sortDir: orderDir,
    }
  );

  const setColumnSortAndOrder = React.useCallback(
    (col: string, dir: "ASC" | "DESC") => {
      setOrderBy(col);
      setOrderDir(dir.toLowerCase() as "asc" | "desc");
    },
    [setOrderBy, setOrderDir]
  );

  return (
    <>
      <div className="container mt-7">
        <OrderListFilterForm />
      </div>
      <div className="container">
        <div className="row row-cards row-deck">
          <div className="col-12">
            <div className="card">
              <div className="card-header border-bottom-0 mt-4 mb-4 justify-content-between">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi card-title"
                >
                  Order list
                </StyledText>
              </div>
              <div className="pl-5 pr-5">
                <Table>
                  <thead>
                    <TableRowHeader>
                      <TableCellHeaderText className="pl-3">
                        ID
                      </TableCellHeaderText>
                      <TableCellHeaderText>Customer</TableCellHeaderText>
                      <TableCellHeaderText>Restaurant</TableCellHeaderText>
                      <TableCellHeaderText style={{ whiteSpace: "nowrap" }}>
                        Order type
                      </TableCellHeaderText>
                      <TableCellHeaderText style={{ whiteSpace: "nowrap" }}>
                        Time of order
                        <SortArrows
                          col="createdAt"
                          currentCol={orderBy}
                          currentOrder={orderDir}
                          setSortColumn={setColumnSortAndOrder}
                        />
                      </TableCellHeaderText>
                      <TableCellHeaderText style={{ whiteSpace: "nowrap" }}>
                        Ordered for
                      </TableCellHeaderText>
                      <TableCellHeaderText style={{ whiteSpace: "nowrap" }}>
                        Order status
                        <SortArrows
                          col="status"
                          currentCol={orderBy}
                          currentOrder={orderDir}
                          setSortColumn={setColumnSortAndOrder}
                        />
                      </TableCellHeaderText>
                      <TableCellHeaderText style={{ whiteSpace: "nowrap" }}>
                        Total price
                      </TableCellHeaderText>
                    </TableRowHeader>
                  </thead>
                  <tbody>
                    {orders?.map((order) => {
                      return <OrderListRow key={order.id} order={order} />;
                    })}
                  </tbody>
                </Table>
              </div>
              <div className="card-footer text-center mr-5 ml-5 px-0">
                {isLoading && <Spinner />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderList;
